import { computed, ref } from 'vue'

export interface Toast {
  id: number
  message: string
}

const TOAST_DURATION_MS = 4000

// Module-level — one shared toast queue for the whole app, so any component
// or composable can surface a confirmation without owning a toast instance.
const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 0

function dismissToast(id: number): void {
  const timer = timers.get(id)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(id)
  }
  const index = toasts.value.findIndex((toast) => toast.id === id)
  if (index !== -1) toasts.value.splice(index, 1)
}

function showToast(message: string): void {
  const id = nextId++
  toasts.value.push({ id, message })
  timers.set(
    id,
    setTimeout(() => dismissToast(id), TOAST_DURATION_MS),
  )
}

// Clears every pending toast and its timer. Used by auth.ts to wipe any
// in-flight toasts on logout/session-expiry, and by tests to keep shared
// module state from bleeding between specs.
function reset(): void {
  ;[...toasts.value].forEach((toast) => dismissToast(toast.id))
}

export function useToast() {
  return {
    toasts: computed(() => toasts.value),
    showToast,
    dismissToast,
    reset,
  }
}
